import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { FaCheckCircle } from 'react-icons/fa';
import DashboardHeader from '../../components/dashboard/DashboardHeader';
import DashboardFooter from '../../components/dashboard/DashboardFooter';
import { notificationsAPI } from '../../utils/api';
import { sanitizeMarkdownHtml } from '../../utils/sanitizeHtml';

const Wrapper = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f5f7fb;
`;

const Container = styled.div`
  flex: 1;
  max-width: 960px;
  margin: 0 auto;
  padding: 40px 20px 60px;

  @media (max-width: 768px) {
    padding: 24px 16px 40px;
  }
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 24px;
`;

const Controls = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const ControlGroup = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const TabsContainer = styled.div`
  display: flex;
  gap: 0;
  background: #fff;
  border-radius: 10px;
  padding: 4px;
  border: 1px solid rgba(148, 163, 184, 0.2);
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.06);
`;

const Tab = styled.button`
  padding: 10px 18px;
  font-weight: 600;
  font-size: 14px;
  border: none;
  background: ${(props) => (props.$active ? 'rgb(255, 107, 53)' : 'transparent')};
  color: ${(props) => (props.$active ? '#fff' : '#374151')};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: 6px;

  &:hover {
    background: ${(props) => (props.active ? 'rgb(229, 96, 47)' : 'rgba(148, 163, 184, 0.1)')};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Button = styled.button`
  background: ${(props) => (props.secondary ? 'rgba(148, 163, 184, 0.1)' : 'rgb(255, 107, 53)')};
  color: ${(props) => (props.secondary ? '#374151' : '#fff')};
  border-radius: 10px;
  padding: 10px 18px;
  font-weight: 600;
  font-size: 14px;
  border: none;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    background: ${(props) => (props.secondary ? 'rgba(148, 163, 184, 0.15)' : 'rgb(229, 96, 47)')};
    box-shadow: ${(props) =>
      props.secondary
        ? '0 6px 18px rgba(148, 163, 184, 0.15)'
        : '0 10px 24px rgba(255, 107, 53, 0.3)'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const Card = styled.div`
  background: #fff;
  border-radius: 16px;
  border: 1px solid rgba(148, 163, 184, 0.18);
  box-shadow: 0 16px 35px rgba(15, 23, 42, 0.08);
  overflow: hidden;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
`;

const Item = styled.div`
  position: relative;
  padding: 18px 22px;
  padding-left: ${(props) => (props.unread ? '22px' : '18px')};
  padding-right: 100px;
  border-bottom: 1px solid rgba(226, 232, 240, 0.7);
  background: ${(props) => (props.unread ? '#f5f5f5' : '#fff')};
  border-left: ${(props) => (props.unread ? '4px solid rgb(255, 107, 53)' : 'none')};

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 600px) {
    padding: 16px 18px;
    padding-right: 90px;
  }
`;

const UnreadIndicator = styled.div`
  position: absolute;
  left: ${(props) => (props.unread ? '8px' : '-10px')};
  top: 50%;
  transform: translateY(-50%);
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: rgb(255, 107, 53);
  opacity: ${(props) => (props.unread ? '1' : '0')};
`;

const ItemHeader = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 8px;
  padding-right: 90px;
`;

const ItemTitle = styled.div`
  font-size: 15px;
  font-weight: ${(props) => (props.unread ? '700' : '600')};
  color: ${(props) => (props.unread ? 'rgb(204, 86, 42)' : '#1f2937')};
  line-height: 1.4;
  flex: 1;
`;

const ItemDate = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  font-size: 11px;
  color: #9ca3af;
  white-space: nowrap;
`;

const ReadButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgb(255, 107, 53);
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgb(229, 96, 47);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(255, 107, 53, 0.3);
  }

  &:active {
    transform: translateY(0);
  }
`;

const ReadStatus = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: #6b7280;
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const ItemMessage = styled.div`
  font-size: 14px;
  color: ${(props) => (props.unread ? '#374151' : '#4b5563')};
  line-height: 1.6;
  margin-bottom: 10px;
  word-wrap: break-word;
`;

const MessagePreview = styled.div`
  display: -webkit-box;
  -webkit-line-clamp: ${(props) => (props.expanded ? 'none' : '3')};
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ExpandButton = styled.button`
  background: none;
  border: none;
  color: rgb(255, 107, 53);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  padding: 4px 0;
  margin-top: 6px;
  transition: color 0.2s ease;

  &:hover {
    color: rgb(204, 86, 42);
    text-decoration: underline;
  }
`;

const EmptyState = styled.div`
  padding: 40px;
  text-align: center;
  color: #6b7280;
  font-size: 15px;
`;

const LoadMore = styled.div`
  padding: 18px 22px;
  display: flex;
  justify-content: center;
  background: #f8fafc;
  border-top: 1px solid rgba(226, 232, 240, 0.7);
`;

const ErrorMessage = styled.div`
  background: rgba(239, 68, 68, 0.08);
  color: #991b1b;
  padding: 14px 16px;
  border-radius: 12px;
  margin-bottom: 20px;
  font-size: 14px;
  border: 1px solid rgba(248, 113, 113, 0.25);
`;

const MAX_PREVIEW_LENGTH = 200;

// Функция для парсинга markdown-разметки в простой HTML
const parseMarkdown = (text) => {
  if (!text) return '';
  
  // Сначала экранируем HTML-теги для безопасности
  let html = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  
  // Применяем форматирование (после экранирования HTML)
  html = html
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')  // Жирный **текст**
    .replace(/\*(.*?)\*/g, '<em>$1</em>')              // Курсив *текст*
    .replace(/~~(.*?)~~/g, '<del>$1</del>')            // Зачеркнутый ~~текст~~
    .replace(/__(.*?)__/g, '<u>$1</u>');               // Подчеркнутый __текст__
  
  // БЕЗОПАСНОСТЬ: Дополнительная санитизация через DOMPurify
  return sanitizeMarkdownHtml(html);
};

const Notifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [unread, setUnread] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [expandedNotifications, setExpandedNotifications] = useState(new Set());
  const [activeTab, setActiveTab] = useState('all');

  const fetchNotifications = async (pageNumber = 1, append = false) => {
    try {
      if (append) {
        setIsLoadingMore(true);
      } else {
        setIsLoading(true);
      }
      const response = await notificationsAPI.getList({ page: pageNumber, limit: 10 });

      setNotifications((prev) =>
        append ? [...prev, ...(response.notifications || [])] : (response.notifications || [])
      );
      setTotal(response.pagination?.total || 0);
      setPages(response.pagination?.pages || 1);
      setUnread(response.unread || 0);
      setError('');
    } catch (err) {
      setError(err.message || 'Не удалось загрузить уведомления');
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchNotifications(1, false);
  }, []);

  const handleLoadMore = () => {
    if (page < pages && !isLoadingMore) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchNotifications(nextPage, true);
    }
  };

  const handleRefresh = () => {
    setPage(1);
    fetchNotifications(1, false);
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationsAPI.markAllRead();
      setNotifications((prev) =>
        prev.map((item) => ({ ...item, status: 'read', read_at: item.read_at || new Date().toISOString() }))
      );
      setUnread(0);
      window.dispatchEvent(new CustomEvent('notifications:refresh'));
    } catch (err) {
      setError(err.message || 'Не удалось обновить уведомления');
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await notificationsAPI.markRead(notificationId);
      setNotifications((prev) =>
        prev.map((item) =>
          item.id === notificationId
            ? { ...item, status: 'read', read_at: item.read_at || new Date().toISOString() }
            : item
        )
      );
      setUnread((prev) => Math.max(0, prev - 1));
      window.dispatchEvent(new CustomEvent('notifications:refresh'));
    } catch (err) {
      setError(err.message || 'Не удалось обновить уведомление');
    }
  };

  const toggleExpand = (notificationId) => {
    setExpandedNotifications((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(notificationId)) {
        newSet.delete(notificationId);
      } else {
        newSet.add(notificationId);
      }
      return newSet;
    });
  };

  const filteredNotifications = activeTab === 'unread'
    ? notifications.filter((item) => item.status === 'unread')
    : notifications;

  const isEmpty = !isLoading && filteredNotifications.length === 0;

  return (
    <Wrapper>
      <DashboardHeader />
      <Container>
        <Title>Все уведомления</Title>

        {error && <ErrorMessage>{error}</ErrorMessage>}

        <Controls>
          <ControlGroup>
            <TabsContainer>
              <Tab $active={activeTab === 'all'} onClick={() => setActiveTab('all')}>
                Все ({total})
              </Tab>
              <Tab $active={activeTab === 'unread'} onClick={() => setActiveTab('unread')}>
                Непрочитанные ({unread})
              </Tab>
            </TabsContainer>
          </ControlGroup>
          <ControlGroup>
            <Button secondary onClick={handleRefresh} disabled={isLoading}>
              Обновить
            </Button>
            <Button onClick={handleMarkAllRead} disabled={unread === 0}>
              Отметить все как прочитанные
            </Button>
          </ControlGroup>
        </Controls>

        <Card>
          {isLoading ? (
            <EmptyState>Загрузка уведомлений...</EmptyState>
          ) : isEmpty ? (
            <EmptyState>Пока нет уведомлений</EmptyState>
          ) : (
            <List>
              {filteredNotifications.map((item) => {
                const isUnread = item.status === 'unread';
                const isExpanded = expandedNotifications.has(item.id);
                const messageLength = item.message?.length || 0;
                const shouldTruncate = messageLength > MAX_PREVIEW_LENGTH;
                const displayMessage = isExpanded || !shouldTruncate 
                  ? item.message 
                  : item.message.substring(0, MAX_PREVIEW_LENGTH) + '...';

                return (
                  <Item key={item.id} unread={isUnread}>
                    <UnreadIndicator unread={isUnread} />
                    <ItemHeader>
                      <ItemTitle unread={isUnread}>{item.title}</ItemTitle>
                      <ItemDate>
                        {new Date(item.created_at).toLocaleString('ru-RU', {
                          day: '2-digit',
                          month: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </ItemDate>
                    </ItemHeader>
                    <ItemMessage unread={isUnread}>
                      {shouldTruncate && !isExpanded ? (
                        <MessagePreview dangerouslySetInnerHTML={{ __html: parseMarkdown(displayMessage) }} />
                      ) : (
                        <div dangerouslySetInnerHTML={{ __html: parseMarkdown(item.message) }} />
                      )}
                      {shouldTruncate && (
                        <ExpandButton onClick={() => toggleExpand(item.id)}>
                          {isExpanded ? 'Скрыть' : 'Показать полностью'}
                        </ExpandButton>
                      )}
                    </ItemMessage>
                    <div style={{ marginTop: '10px' }}>
                      {isUnread ? (
                        <ReadButton onClick={() => handleMarkAsRead(item.id)}>
                          <FaCheckCircle />
                          Прочитал
                        </ReadButton>
                      ) : (
                        <ReadStatus>
                          <FaCheckCircle style={{ color: '#9ca3af' }} />
                          Прочитано
                        </ReadStatus>
                      )}
                    </div>
                  </Item>
                );
              })}
              {page < pages && (
                <LoadMore>
                  <Button onClick={handleLoadMore} secondary disabled={isLoadingMore}>
                    {isLoadingMore ? 'Загрузка...' : 'Показать ещё'}
                  </Button>
                </LoadMore>
              )}
            </List>
          )}
        </Card>
      </Container>
      <DashboardFooter />
    </Wrapper>
  );
};

export default Notifications;

